// Google public domain list, checked 2026-09-05:
// https://www.google.com/supported_domains
// Snapshot locally: attribution must never wait for a third-party request.
const GOOGLE_SEARCH_DOMAINS = new Set(`
google.com google.ad google.ae google.com.af google.com.ag google.al google.am google.co.ao
google.com.ar google.as google.at google.com.au google.az google.ba google.com.bd google.be
google.bf google.bg google.com.bh google.bi google.bj google.com.bn google.com.bo google.com.br
google.bs google.bt google.co.bw google.by google.com.bz google.ca google.cd google.cf
google.cg google.ch google.ci google.co.ck google.cl google.cm google.cn google.com.co
google.co.cr google.com.cu google.cv google.com.cy google.cz google.de google.dj google.dk
google.dm google.com.do google.dz google.com.ec google.ee google.com.eg google.es google.com.et
google.fi google.com.fj google.fm google.fr google.ga google.ge google.gg google.com.gh
google.com.gi google.gl google.gm google.gr google.com.gt google.gy google.com.hk google.hn
google.hr google.ht google.hu google.co.id google.ie google.co.il google.im google.co.in
google.iq google.is google.it google.je google.com.jm google.jo google.co.jp google.co.ke
google.com.kh google.ki google.kg google.co.kr google.com.kw google.kz google.la google.com.lb
google.li google.lk google.co.ls google.lt google.lu google.lv google.com.ly google.co.ma
google.md google.me google.mg google.mk google.ml google.com.mm google.mn google.com.mt
google.mu google.mv google.mw google.com.mx google.com.my google.co.mz google.com.na google.com.ng
google.com.ni google.ne google.nl google.no google.com.np google.nr google.nu google.co.nz
google.com.om google.com.pa google.com.pe google.com.pg google.com.ph google.com.pk google.pl google.pn
google.com.pr google.ps google.pt google.com.py google.com.qa google.ro google.ru google.rw
google.com.sa google.com.sb google.sc google.se google.com.sg google.sh google.si google.sk
google.com.sl google.sn google.so google.sm google.sr google.st google.com.sv google.td
google.tg google.co.th google.com.tj google.tl google.tm google.tn google.to google.com.tr
google.tt google.com.tw google.co.tz google.com.ua google.co.ug google.co.uk google.com.uy google.co.uz
google.com.vc google.co.ve google.co.vi google.com.vn google.vu google.ws google.rs google.co.za
google.co.zm google.co.zw google.cat
`.trim().split(/\s+/));

export type OrganicSearchSource = 'google' | 'bing' | 'duckduckgo' | 'yahoo';

export type AiReferralSource = 'chatgpt' | 'perplexity' | 'gemini' | 'copilot' | 'claude';

// Exact app hosts, not all of google.com / microsoft.com or arbitrary subdomains.
const AI_SOURCE_ALIASES: Record<string, AiReferralSource> = {
  chatgpt: 'chatgpt', 'chatgpt.com': 'chatgpt', 'chat.openai.com': 'chatgpt',
  perplexity: 'perplexity', 'perplexity.ai': 'perplexity',
  gemini: 'gemini', 'gemini.google.com': 'gemini',
  copilot: 'copilot', 'copilot.microsoft.com': 'copilot',
  claude: 'claude', 'claude.ai': 'claude',
};

export function resolveAiSourceAlias(source: string | null): AiReferralSource | null {
  const key = source?.trim().toLowerCase().replace(/^www\./, '') ?? '';
  return Object.prototype.hasOwnProperty.call(AI_SOURCE_ALIASES, key) ? AI_SOURCE_ALIASES[key] : null;
}

export function resolveAiReferrer(referrer: string | null): AiReferralSource | null {
  if (!referrer) return null;
  try {
    const url = new URL(referrer);
    if (!['http:', 'https:'].includes(url.protocol)) return null;
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    return host.includes('.') ? resolveAiSourceAlias(host) : null;
  } catch {
    return null;
  }
}

export function resolveOrganicSearchReferrer(referrer: string | null): OrganicSearchSource | null {
  if (!referrer) return null;
  try {
    const url = new URL(referrer);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    // Do not classify Gmail, Docs, Maps, or arbitrary Google subdomains as search.
    if (GOOGLE_SEARCH_DOMAINS.has(host) && ['/', '/search', '/webhp', '/url', '/imgres'].includes(url.pathname)) return 'google';
    if (['bing.com', 'cn.bing.com'].includes(host) && ['/', '/search', '/images/search'].includes(url.pathname)) return 'bing';
    if (host === 'duckduckgo.com' && ['/', '/html/', '/lite/'].includes(url.pathname)) return 'duckduckgo';
    if ((host === 'search.yahoo.com' || /^[a-z]{2}\.search\.yahoo\.com$/.test(host)) && ['/', '/search'].includes(url.pathname)) return 'yahoo';
    return null;
  } catch {
    return null;
  }
}
